<template lang="pug">
  div(class="ai-feedback")
    div(class="d-flex align-center justify-center mb-4")
      img(
        src="~/assets/images/target.gif"
        alt="target"
        class="ai-feedback__title-emoji"
      )
    h2(class="title-32px bold mb-2") {{ $t('How do you like the AI assistant?') }}
    div(class="text-14px mb-6") {{ $t('Your feedback helps us improve the automatic field creation') }}
    div(class="ai-feedback__rating mb-6")
      div(
        v-for="(rating, index) in ratings"
        :key="index"
        class="ai-feedback__rating-item"
        :class="{ 'ai-feedback__rating-item--selected': selectedRating === rating.value }"
        @click="selectRating(rating.value)"
      )
        div(class="ai-feedback__emoji") {{ rating.emoji }}
        div(class="ai-feedback__rating-text text-12px") {{ $t(rating.text) }}

    div(
      v-if="selectedRating"
      class="ai-feedback__additional-questions"
    )
      div(
        v-if="shouldShowQuestions"
        class="ai-feedback__questions mb-6"
      )
        h3(class="text-16px bold mb-4") {{ questionTitle }}
        v-btn-toggle(
          v-model="selectedOptions"
          multiple
          class="ai-feedback__question-options"
        )
          v-btn(
            v-for="option in questionOptions"
            :key="option"
            :value="option"
            depressed
            class="ai-feedback__option-btn"
            small
          ) {{ option }}

      v-btn(
        class="main-button"
        block
        :loading="submitLoading"
        @click="submitFeedback"
      ) {{ $t('Send feedback') }}
</template>

<script>
export default {
  name: 'BlockAiFeedback',
  props: {
    closeModal: {
      type: Function,
      default: null
    },
    onFeedbackGiven: {
      type: Function,
      default: null
    }
  },
  data() {
    return {
      selectedRating: null,
      selectedOptions: [],
      submitLoading: false,
      ratings: [
        { value: '1', emoji: '😠', text: 'Terrible' },
        { value: '2', emoji: '😔', text: 'Bad' },
        { value: '3', emoji: '😐', text: 'Normal' },
        { value: '4', emoji: '🙂', text: 'Good' },
        { value: '5', emoji: '😍', text: 'Excellent' }
      ]
    }
  },
  computed: {
    shouldShowQuestions() {
      return this.selectedRating && !['1', '5'].includes(this.selectedRating)
    },

    questionConfig() {
      const configs = {
        2: {
          title: 'Tell us more about AI performance',
          options: [
            'Missed fields',
            'Created extra fields',
            'Inaccurate field names',
            'Speed of work'
          ]
        },
        3: {
          title: 'What needs improvement?',
          options: ['Field names', 'Missed some fields', 'Wrong types', 'Placement']
        },
        4: {
          title: 'What can be improved?',
          options: ['Field names', 'Field types', 'Field placement', 'Speed of work']
        }
      }
      return configs[this.selectedRating] || { title: '', options: [] }
    },

    questionTitle() {
      return this.$t(this.questionConfig.title)
    },

    questionOptions() {
      return this.questionConfig.options.map(option => this.$t(option))
    }
  },
  methods: {
    selectRating(value) {
      this.selectedRating = value
    },

    createFormData() {
      const data = new FormData()
      data.append('created_at', this.$moment().format('DD.MM.YYYY HH:mm:ss'))
      data.append('rating', this.selectedRating)
      data.append('ratingText', this.ratings.find(r => r.value === this.selectedRating)?.text || '')
      data.append('selectedOptions', this.selectedOptions.join(', '))
      if (this.$Organization) {
        data.append('edrpou', this.$Organization.edrpou || '')
        data.append('organization', this.$Organization.name || '')
      }
      return data
    },

    async submitFeedback() {
      if (!this.selectedRating) return
      this.submitLoading = true
      try {
        const GOOGLE_SCRIPT_URL =
          'https://script.google.com/macros/s/AKfycby9swPkw2GhRndN4mKm2PnE4mBFA44dQpPDQeBqY_IOxcV3kLdiQnZ-wB-aH2RnO_Ljhw/exec'
        await fetch(GOOGLE_SCRIPT_URL, {
          method: 'POST',
          body: this.createFormData()
        })
        this.$notification.success(this.$t('Feedback sent successfully'))
        this.handleFeedbackComplete()
      } catch (error) {
        this.$notification.error(error.message)
        this.handleFeedbackComplete()
      } finally {
        this.submitLoading = false
      }
    },

    handleFeedbackComplete() {
      if (this.onFeedbackGiven) {
        this.onFeedbackGiven()
      } else {
        this.closeDialog()
      }
    },

    closeDialog() {
      if (this.closeModal) {
        this.closeModal()
      }
    }
  }
}
</script>

<style scoped lang="scss">
.ai-feedback {
  padding: 40px;
  &__title-emoji {
    width: 80px;
    height: 80px;
    object-fit: contain;
  }

  &__rating {
    display: flex;
    justify-content: center;
    gap: 8px;
  }

  &__rating-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    transition: all 0.3s ease;

    &:not(.ai-feedback__rating-item--selected) {
      .ai-feedback__rating:has(.ai-feedback__rating-item--selected) & {
        opacity: 0.3;
      }
    }
  }

  &__rating-text {
    transition: all 0.3s ease;
  }

  &__emoji {
    font-size: 40px;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    border: 1px solid transparent;
    background-color: $background-gray;
    &:hover {
      border-color: $primary;
    }
    .ai-feedback__rating-item--selected & {
      background-color: $primary-light;
      border-color: $primary;
    }
  }
  &__questions {
    text-align: left;
  }

  &__question-options {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__option-btn {
    background-color: transparent !important;
    border: 1px solid $border-gray !important;
    border-radius: 15px !important;
    font-size: 12px !important;
    font-weight: 400 !important;
  }
}
</style>
